import './loads-filter.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { ButtonProps } from '@celebration/react/ClbButton/ClbButton.types';
import ILoadsFiltersProps from './interfaces/ILoadsFiltersProps';
import ILoadsFiltersModel from '../../interfaces/IFiltersModel';
import ISelectInputModel from '../load-filters/load-filter-advanced/components/location-filter/interfaces/ISelectInputModel';
import { getSelectOptionsEnumLoadStatus } from '../../../pages/business/loads/utils/loads-utils';
import LoadsBulkAction from '../../../pages/business/loads/enums/loads-bulk-action-type.enum';
import ILoadsLocationModel from '../../interfaces/loads/ILoadsLocationModel';
import LoadManager from '../../services/loadmanager';
import LoadsFilterLocationType from '../../enums/loads/loads-filter-tags-location-type.enum';
import LoadsConstants from '../../../pages/business/loads/constants';
import ILoadsLocationResponse from '../load-filters/interfaces/ILoadsLocationResponse';
import { getLabelDescription } from '../../../utils/string-utils';
import ILoadMaterialResponse from '../load-filters/interfaces/ILoadMaterialResponse';
import {
    setChannelFilterProps,
    setDateFilterProps,
    setDestinationLocationFilterProps,
    setMaterialFilterProps,
    setSourceLocationFilterProps,
} from '../../utils/load-offers-utils';
import { getLoadChannelOptions } from '../../enums/loads/load-channel-types.enum';
import LoadFilterSimple from '../load-filters/load-filter-simple/load-filter-simple';
import LoadFilterAdvanced from '../load-filters/load-filter-advanced/load-filter-advanced';
import LoadActionBulkButtons from '../load-filters/load-action-bulk-buttons/load-action-bulk-buttons';
import LoadsBulkActionConfirmation from '../../../pages/business/loads/components/loads-bulk-action-confirmation';
import LoadFilterTags from '../load-filters/load-filter-tags/load-filter-tags';
import { getLoadCategoryOptions } from '../../enums/loads/load-category.enum';

function LoadsFilters({
    filters,
    selectedCards,
    onApplyFilters,
    onSelectAllCards,
    onCleanCardSelection,
    fetchFirstPage,
    isPool = false,
}: Readonly<ILoadsFiltersProps>) {
    const { t } = useTranslation();
    const [openAdvancedFilter, setOpenAdvancedFilter] =
        useState<boolean>(false);

    const [advancedFilter, setAdvancedFilter] = useState<
        Partial<ILoadsFiltersModel>
    >(filters.current);
    const [materialOptions, setMaterialOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );
    const [sourceUFOptions, setSourceUFOptions] = useState<ISelectInputModel[]>(
        [] as ISelectInputModel[],
    );
    const [destinationUFOptions, setDestinationUFOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);
    const [sourceDescriptionOptions, setSourceDescriptionOptions] = useState<
        ISelectInputModel[]
    >([] as ISelectInputModel[]);
    const [destinationDescriptionOptions, setDestinationDescriptionOptions] =
        useState<ISelectInputModel[]>([] as ISelectInputModel[]);
    const [bulkAction, setBulkAction] = useState<LoadsBulkAction | undefined>(
        undefined,
    );
    const advancedFilters = useRef<ILoadsFiltersModel>(
        {} as ILoadsFiltersModel,
    );

    const location = useLocation();
    const [searchParams] = useSearchParams();
    const fetchInitialData = useRef(true);

    const setAdvancedFilters = useCallback(
        (filtersModel: ILoadsFiltersModel) => {
            advancedFilters.current = filtersModel;
            setAdvancedFilter(filtersModel);
        },
        [setAdvancedFilter],
    );

    const openAdvancedFilterDrawer = () => {
        setOpenAdvancedFilter(true);
        setAdvancedFilter(filters.current);
    };

    const closeAdvancedFilterDrawer = useCallback(() => {
        advancedFilters.current = {};
        setOpenAdvancedFilter(false);
        setAdvancedFilter(filters.current);
    }, [filters]);

    const getLoadStatusOptions = () => {
        const status = getSelectOptionsEnumLoadStatus(isPool);

        status.forEach(s => {
            s.label = t(s.label);
            return s;
        });

        return status;
    };

    const cleanCurrentFilters = useCallback(() => {
        onApplyFilters({});
        closeAdvancedFilterDrawer();
        onCleanCardSelection();
        fetchFirstPage();
    }, [
        closeAdvancedFilterDrawer,
        fetchFirstPage,
        onApplyFilters,
        onCleanCardSelection,
    ]);

    const onApplyAdvancedFilters = useCallback(() => {
        filters.current = advancedFilters.current;
        closeAdvancedFilterDrawer();
        fetchFirstPage();
    }, [filters, closeAdvancedFilterDrawer, fetchFirstPage]);

    const setFilters = useCallback(
        (filtersModel: ILoadsFiltersModel) => {
            onApplyFilters(filtersModel);
        },
        [onApplyFilters],
    );

    const getLoadsLocation = useCallback(
        async (filter: ILoadsLocationModel) => {
            return LoadManager.getLoadsLocation(filter);
        },
        [],
    );

    const mapLocationResponse = useCallback(
        (location: any): ILoadsLocationResponse => ({
            code: location.Code || location.code,
            description: location.Description || location.description,
            address: location.Address || location.address,
            city: location.City || location.city,
            federativeUnit: location.FederativeUnit || location.federativeUnit,
        }),
        [],
    );

    const handleLocationSearch = useCallback(
        (locationType: LoadsFilterLocationType, value: string) => {
            const locationService = isPool
                ? LoadManager.getPoolLocations
                : getLoadsLocation;

            return locationService({
                locationType,
                search: value,
                _page: LoadsConstants.INITIAL_PAGE_NUMBER,
                _size: 10,
            }).then(({ response }) => {
                return response ? response.map(mapLocationResponse) : response!;
            });
        },
        [getLoadsLocation, mapLocationResponse, isPool],
    );

    const handleSourceDescriptionSearch = useCallback(
        (value: string) =>
            handleLocationSearch(LoadsFilterLocationType.Source, value),
        [handleLocationSearch],
    );

    const handleDestinationDescriptionSearch = useCallback(
        (value: string) =>
            handleLocationSearch(LoadsFilterLocationType.Destination, value),
        [handleLocationSearch],
    );

    const handleMaterialSearch = useCallback(
        async (value: string) => {
            const materialService = isPool
                ? () => LoadManager.getPoolMaterials(value)
                : () => LoadManager.getLoadsMaterial(value);

            return materialService().then(({ response }) => response!);
        },
        [isPool],
    );

    const getUFResultOptions = useCallback(
        (states: string[]) => {
            const options = [] as ISelectInputModel[];

            const resultSet = new Set(states);
            resultSet.forEach(
                (item, index) => {
                    options.push({
                        key: index?.toString(),
                        label: item,
                        value: item,
                    });
                },
                [getLoadsLocation],
            );

            return options;
        },
        [getLoadsLocation],
    );

    const processLocationResults = useCallback(
        (
            locationsResults: any[],
            setUFOptions: (options: ISelectInputModel[]) => void,
            handleDescriptionSearchResult: (
                data: ILoadsLocationResponse[],
            ) => void,
            locationsStatesResults: any[],
        ) => {
            const mappedResults = locationsResults.map(mapLocationResponse);
            setUFOptions(getUFResultOptions(locationsStatesResults));
            handleDescriptionSearchResult(mappedResults);
        },
        [mapLocationResponse, getUFResultOptions],
    );

    const handleSourceDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const sourceDescriptions = [] as ISelectInputModel[];

            data.forEach((sourceDesc, index) => {
                sourceDescriptions.push({
                    key: index?.toString(),
                    label: getLabelDescription(
                        sourceDesc.description,
                        sourceDesc.code,
                    ),
                    value: sourceDesc.code,
                });
            });

            setSourceDescriptionOptions(sourceDescriptions);
        },
        [setSourceDescriptionOptions],
    );

    const handleDestinationDescriptionSearchResult = useCallback(
        (data: ILoadsLocationResponse[]) => {
            const destOptions: ISelectInputModel[] = [];

            data.forEach((item, index) => {
                destOptions.push({
                    key: index.toString(),
                    label: getLabelDescription(item.description, item.code),
                    value: item.code,
                });
            });

            setDestinationDescriptionOptions(destOptions);
        },
        [setDestinationDescriptionOptions],
    );

    const handleMaterialSearchResult = useCallback(
        (data: ILoadMaterialResponse[]) => {
            const selectOptions = [] as ISelectInputModel[];

            if (!data) {
                setMaterialOptions(selectOptions);
                return;
            }

            const options = new Set(data.map(mat => mat));

            options.forEach(mat => {
                selectOptions.push({
                    key: mat.id?.toString(),
                    label: mat.name,
                    value: mat.id,
                });
            });

            setMaterialOptions(selectOptions);
        },
        [setMaterialOptions],
    );

    const fetchSourceLocationsFilterValues = useCallback(async () => {
        const promises: Promise<any>[] = [];

        promises.push(
            LoadManager.getLoadsLocation({
                locationType: LoadsFilterLocationType.Source,
            } as ILoadsLocationModel),
        );

        promises.push(
            LoadManager.getLoadsLocationStates(LoadsFilterLocationType.Source),
        );

        const [
            { response: locationsResults },
            { response: locationsStatesResults },
        ] = await Promise.all(promises);

        if (locationsResults) {
            processLocationResults(
                locationsResults,
                setSourceUFOptions,
                handleSourceDescriptionSearchResult,
                locationsStatesResults,
            );
        }
    }, [processLocationResults, handleSourceDescriptionSearchResult]);

    const fetchDestinationLocationsFilterValues = useCallback(async () => {
        const promises: Promise<any>[] = [];

        promises.push(
            LoadManager.getLoadsLocation({
                locationType: LoadsFilterLocationType.Destination,
            } as ILoadsLocationModel),
        );

        promises.push(
            LoadManager.getLoadsLocationStates(
                LoadsFilterLocationType.Destination,
            ),
        );

        const [
            { response: locationsResults },
            { response: locationsStatesResults },
        ] = await Promise.all(promises);

        if (locationsResults) {
            processLocationResults(
                locationsResults,
                setDestinationUFOptions,
                handleDestinationDescriptionSearchResult,
                locationsStatesResults,
            );
        }
    }, [processLocationResults, handleDestinationDescriptionSearchResult]);

    const fetchMaterialsFilterValues = useCallback(async () => {
        const { response } = await LoadManager.getLoadsMaterial(
            undefined,
            LoadsConstants.INITIAL_PAGE_NUMBER,
            LoadsConstants.PAGE_SIZE,
        );

        handleMaterialSearchResult(response!);
    }, [handleMaterialSearchResult]);

    const fetchPoolFilterValues = useCallback(async () => {
        if (isPool) {
            try {
                // Carregar localizações de origem para pool
                const sourceLocations = await LoadManager.getPoolLocations({
                    locationType: LoadsFilterLocationType.Source,
                } as ILoadsLocationModel);

                if (sourceLocations.response) {
                    const sourceStates = sourceLocations.response.map(
                        location => location.federativeUnit,
                    );
                    setSourceUFOptions(getUFResultOptions(sourceStates));
                    handleSourceDescriptionSearchResult(
                        sourceLocations.response,
                    );
                }

                // Carregar localizações de destino para pool
                const destinationLocations = await LoadManager.getPoolLocations(
                    {
                        locationType: LoadsFilterLocationType.Destination,
                    } as ILoadsLocationModel,
                );

                if (destinationLocations.response) {
                    const destinationStates = destinationLocations.response.map(
                        location => location.federativeUnit,
                    );
                    setDestinationUFOptions(
                        getUFResultOptions(destinationStates),
                    );
                    handleDestinationDescriptionSearchResult(
                        destinationLocations.response,
                    );
                }

                // Carregar materiais para pool
                const materials = await LoadManager.getPoolMaterials();
                if (materials.response) {
                    handleMaterialSearchResult(materials.response);
                }
            } catch (error) {
                console.error('Error loading pool filter values:', error);
            }
        }
    }, [
        isPool,
        getUFResultOptions,
        handleSourceDescriptionSearchResult,
        handleDestinationDescriptionSearchResult,
        handleMaterialSearchResult,
    ]);

    const onConfirmBulkAction = () => {
        setBulkAction(undefined);
        fetchFirstPage();
    };

    const dateFilterProps = setDateFilterProps(
        advancedFilter,
        setAdvancedFilters,
        undefined,
        31,
    );

    const channelFilterProps = setChannelFilterProps(
        advancedFilter,
        setAdvancedFilters,
        getLoadChannelOptions,
    );

    const materialFilterProps = setMaterialFilterProps(
        advancedFilter,
        setAdvancedFilters,
        handleMaterialSearch,
        handleMaterialSearchResult,
        materialOptions,
    );

    const sourceLocationFilterProps = setSourceLocationFilterProps(
        advancedFilter,
        sourceUFOptions,
        setAdvancedFilters,
        sourceDescriptionOptions,
        handleSourceDescriptionSearch,
        handleSourceDescriptionSearchResult,
    );

    const destinationLocationFilterProps = setDestinationLocationFilterProps(
        advancedFilter,
        destinationUFOptions,
        setAdvancedFilters,
        destinationDescriptionOptions,
        handleDestinationDescriptionSearch,
        handleDestinationDescriptionSearchResult,
    );

    useEffect(() => {
        if (fetchInitialData.current) {
            if (isPool) {
                fetchPoolFilterValues();
            } else {
                fetchSourceLocationsFilterValues();
                fetchDestinationLocationsFilterValues();
                fetchMaterialsFilterValues();
            }

            if (searchParams.getAll(LoadsConstants.LOAD_ID_KEY).length > 0) {
                filters.current.loadId = [
                    ...searchParams.getAll(LoadsConstants.LOAD_ID_KEY),
                ];
                window.history.replaceState({}, '', location.pathname);
            }

            fetchInitialData.current = false;
        }
    }, [
        fetchSourceLocationsFilterValues,
        fetchDestinationLocationsFilterValues,
        fetchMaterialsFilterValues,
        fetchPoolFilterValues,
        openAdvancedFilter,
        fetchInitialData,
        filters,
        location.pathname,
        searchParams,
        isPool,
    ]);

    return (
        <div className="loads-grid-filters">
            <div className="loads-filters-container">
                <LoadFilterSimple
                    filters={filters}
                    setOpenAdvancedFilter={openAdvancedFilterDrawer}
                    setFilters={setFilters}
                    getLoadStatusOptions={getLoadStatusOptions}
                    getLoadChannelOptions={getLoadChannelOptions}
                    getLoadCategoryOptions={
                        isPool ? undefined : getLoadCategoryOptions
                    }
                />
                <LoadFilterAdvanced
                    open={openAdvancedFilter}
                    onClose={closeAdvancedFilterDrawer}
                    onApply={onApplyAdvancedFilters}
                    onClear={cleanCurrentFilters}
                    dateFilterProps={dateFilterProps()}
                    channelFilterProps={channelFilterProps()}
                    materialFilterProps={materialFilterProps()}
                    sourceLocationFilterProps={sourceLocationFilterProps()}
                    destinationLocationFilterProps={destinationLocationFilterProps()}
                />
                <LoadActionBulkButtons
                    visible={selectedCards.length > 1}
                    buttons={
                        [
                            {
                                id: 'loads-filters__suspend-btn',
                                size: 'md',
                                styleType: 'secondary',
                                label: t('Suspender'),
                                onClick: () =>
                                    setBulkAction(LoadsBulkAction.Suspend),
                            },
                            {
                                id: 'loads-filters__reoffer-btn',
                                size: 'md',
                                styleType: 'secondary',
                                label: t('Reofertar'),
                                onClick: () =>
                                    setBulkAction(LoadsBulkAction.Reoffer),
                            },
                            {
                                id: 'loads-filters__prioritize-btn',
                                size: 'md',
                                styleType: 'secondary',
                                label: t('Priorizar'),
                                onClick: () =>
                                    setBulkAction(LoadsBulkAction.Prioritize),
                            },
                        ] as ButtonProps[]
                    }
                />
                {bulkAction !== undefined && (
                    <LoadsBulkActionConfirmation
                        data-testid="loads-bulk-action-confirmation-modal"
                        key="loads-bulk-action-confirmation-modal"
                        action={bulkAction}
                        selectedLoads={selectedCards}
                        onConfirm={onConfirmBulkAction}
                        onCancel={() => setBulkAction(undefined)}
                    />
                )}
            </div>
            <LoadFilterTags
                filters={filters}
                selectedCards={selectedCards}
                materialOptions={materialOptions}
                onSelectAllCards={onSelectAllCards}
                onCleanCardSelection={onCleanCardSelection}
                cleanCurrentFilters={cleanCurrentFilters}
                onApplyFilters={onApplyFilters}
            />
        </div>
    );
}

export default LoadsFilters;
